import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useConsent } from '../../consent/ConsentContext';
import { isPublicThemePath } from '../../util/marketingRoutes';
import { bootAnonymousIntercom, shutdownIntercom } from '../../util/intercom';
import { isCodeRoute, isTokenBearingRoute } from '../../util/redactUrl';

/**
 * Sitewide, consent-gated boot of the anonymous Intercom messenger (GDPR plan
 * P2-4). Mounted once in _app.js.
 *
 * This used to live in pages/index.js, so the messenger only ever booted for
 * visitors who entered through the homepage. Anyone landing on /pricing,
 * /solutions/*, a blog post or /login got no chat, and the "Chat with us"
 * buttons (Footer, SoftCTASection) silently no-op'd — they call
 * `Intercom('show')` on a messenger that was never booted.
 *
 * Covers every public page: the website plus the sign-in/sign-up flow, where
 * "I can't log in" is exactly when someone needs support. Dashboard routes are
 * Layout1's job — it boots the messenger IDENTIFIED — and bootAnonymousIntercom
 * no-ops once someone is identified, so a signed-in user clicking back to the
 * website is not downgraded to a nameless lead.
 */
export default function ConsentedIntercom() {
  const { asPath, pathname } = useRouter();
  const { ready, has } = useConsent();
  const marketing = has('marketing');
  // asPath carries ?query and #hash; the auth-route matcher compares whole paths.
  //
  // ☠️ NOT on the token-bearing auth pages (D18): /reset-password?code= (reset
  // AND teammate invites), /magic-login?loginToken=, /connect/[provider]/
  // redirect?access_token=&id_token=. They sit inside AUTH_ROUTE_PREFIXES, and
  // the messenger records the URL of the page it runs on — a live reset code
  // would reach Intercom and every agent who can see the lead. Those pages are
  // entered by a full load from an email or an OAuth provider, so skipping the
  // boot costs no support reach. Shutdown below stays unscoped.
  // Checked on the route pattern AND the real path, so a router that has not
  // resolved `pathname` yet still cannot boot on one.
  const path = asPath.split(/[?#]/)[0];
  const tokenPage =
    isTokenBearingRoute(pathname) || isTokenBearingRoute(path) || isCodeRoute(path);
  const publicPage = isPublicThemePath(path) && !tokenPage;

  useEffect(() => {
    // Before the provider has read the cookie, `marketing` is false for
    // everyone — don't mistake that for a withdrawal.
    if (!ready) return;
    // Shutdown is deliberately NOT scoped to public pages. No messenger may run
    // without marketing consent on ANY route, and only Layout1 listens for
    // withdrawal — the Centered routes (/onboarding, /session-conflict) don't,
    // so a messenger booted on the website and carried there by client-side
    // navigation would otherwise outlive the withdrawal. It can't cost Layout1
    // its identified session: Layout1 only identifies with marketing consent.
    if (!marketing) {
      shutdownIntercom();
      return;
    }
    // Not skipping the boot is not enough: a messenger booted on /login and
    // carried to a token page by client-side navigation would record that URL.
    if (tokenPage) {
      shutdownIntercom();
      return;
    }
    if (publicPage) bootAnonymousIntercom();
  }, [ready, marketing, publicPage, tokenPage]);

  return null;
}
